/* ═══════════════════════════════════════════════════════════
   tessara-collection.js — Filter sidebar, sort, infinite scroll.
   Vanilla ES2020.
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ════════════════════════════════════
     Sort — preserve across filter changes
  ════════════════════════════════════ */
  const sortSelect = document.querySelector('.tds-sort-select');
  sortSelect?.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });

  if (sortSelect) {
    const params      = new URLSearchParams(window.location.search);
    const currentSort = params.get('sort_by');
    if (currentSort) {
      const opt = sortSelect.querySelector(`option[value="${currentSort}"]`);
      if (opt) opt.selected = true;
    }
  }

  /* ════════════════════════════════════
     Mobile filter drawer
  ════════════════════════════════════ */
  const filterDrawer  = document.querySelector('[data-filter-drawer]');
  const filterOverlay = document.querySelector('[data-filter-overlay]');
  const filterOpen    = document.querySelector('[data-filter-drawer-toggle]');
  const filterClose   = document.querySelector('[data-filter-drawer-close]');

  function openFilterDrawer() {
    filterDrawer?.setAttribute('data-open', 'true');
    filterOverlay?.setAttribute('data-open', 'true');
    filterDrawer?.removeAttribute('aria-hidden');
    filterOpen?.setAttribute('aria-expanded', 'true');
  }

  function closeFilterDrawer() {
    filterDrawer?.setAttribute('data-open', 'false');
    filterOverlay?.setAttribute('data-open', 'false');
    filterOpen?.setAttribute('aria-expanded', 'false');
  }

  filterOpen?.addEventListener('click', openFilterDrawer);
  filterClose?.addEventListener('click', closeFilterDrawer);
  filterOverlay?.addEventListener('click', closeFilterDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeFilterDrawer();
  });

  /* ════════════════════════════════════
     Accordion "Show N more" toggle
  ════════════════════════════════════ */
  document.querySelectorAll('[data-show-more]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));

      const overflow = btn.parentElement?.querySelector('[data-filter-overflow]');
      if (overflow) {
        if (expanded) {
          overflow.classList.add('hidden');
          overflow.style.display = '';
        } else {
          overflow.classList.remove('hidden');
          overflow.style.display = 'flex';
        }
      }

      btn.querySelector('[data-show-more-text]')?.classList.toggle('hidden', !expanded);
      btn.querySelector('[data-show-less-text]')?.classList.toggle('hidden', expanded);
    });
  });

  /* ════════════════════════════════════
     S&D filter form — auto-submit on checkbox change
  ════════════════════════════════════ */
  document.querySelectorAll('[data-filter-form] input[type="checkbox"]').forEach((cb) => {
    cb.addEventListener('change', () => cb.closest('form').submit());
  });

  /* ════════════════════════════════════
     URL length guard
  ════════════════════════════════════ */
  document.querySelectorAll('.tds-filter-link, input[type="checkbox"][data-filter]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const targetUrl = el.dataset.href || el.closest('a')?.href;
      if (targetUrl && targetUrl.length > 1800) {
        e.preventDefault();
        const warning = document.querySelector('.tds-url-warning') || createWarning();
        warning.hidden = false;
      }
    });
  });

  function createWarning() {
    const div = document.createElement('div');
    div.className     = 'tds-url-warning';
    div.textContent   = 'Too many filters active — please remove some.';
    div.setAttribute('role', 'alert');
    const grid = document.querySelector('.tds-collection-main') || document.body;
    grid.insertAdjacentElement('afterbegin', div);
    return div;
  }

  /* ════════════════════════════════════
     Infinite scroll (pagination sentinel)
  ════════════════════════════════════ */
  const sentinel = document.getElementById('tds-pagination-sentinel');
  if (sentinel) {
    const observer = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(sentinel);

      const nextPage = parseInt(sentinel.dataset.nextPage);
      if (!nextPage) return;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('page', nextPage);
        url.searchParams.set('sections', 'main-collection');
        const res  = await fetch(url.toString());
        const data = await res.json();
        const html = data['main-collection'];

        const tmp = document.createElement('div');
        tmp.innerHTML = html;

        const newCards = tmp.querySelectorAll('.tds-product-card');
        const grid     = document.querySelector('.tds-product-grid');
        if (grid && newCards.length > 0) {
          newCards.forEach((card) => grid.appendChild(card));
        }

        const newSentinel = tmp.querySelector('#tds-pagination-sentinel');
        if (newSentinel) {
          sentinel.dataset.nextPage = newSentinel.dataset.nextPage;
          observer.observe(sentinel);
        } else {
          sentinel.remove();
        }
      } catch {
        // Fall back to standard pagination link
      }
    }, { rootMargin: '200px' });

    observer.observe(sentinel);
  }

  /* Sidebar sticky: handled by CSS position:sticky in tessara-layout.css.
     JS sticky was removed (2026-04-13) — no overflow:hidden exists on any
     ancestor, so CSS sticky works cleanly. Do not re-add JS translateY sticky;
     it causes visible repositioning on each scroll event. */

  /* ════════════════════════════════════
     Aria live: announce filter results
  ════════════════════════════════════ */
  const resultCount = document.querySelector('[data-results-count]');
  if (resultCount) {
    const live = document.createElement('div');
    live.setAttribute('aria-live', 'polite');
    live.className   = 'sr-only';
    live.textContent = resultCount.textContent;
    document.body.appendChild(live);
  }

})();

/* ═══════════════════════════════════════════════════════════
   Collections index preview — sort chips + analytics.
   Guarded: only runs on /collections?view=preview.
   At cutover, remove the classList guard and this comment.
   ═══════════════════════════════════════════════════════════ */

if (document.body.classList.contains('list-collections-preview')) {
  (function () {
    'use strict';

    var grid = document.querySelector('.tds-collection-grid');

    function sortGrid(sortBy) {
      if (!grid) return;
      try {
        var cards = Array.from(grid.querySelectorAll('[data-handle]'));
        if (sortBy === 'alpha') {
          cards.sort(function (a, b) {
            return (a.dataset.title || '').localeCompare(b.dataset.title || '');
          });
        } else if (sortBy === 'most_products') {
          cards.sort(function (a, b) {
            return parseInt(b.dataset.productCount || 0) - parseInt(a.dataset.productCount || 0);
          });
        } else {
          cards.sort(function (a, b) {
            return parseInt(b.dataset.created || 0) - parseInt(a.dataset.created || 0);
          });
        }
        cards.forEach(function (card) { grid.appendChild(card); });
      } catch (e) {
        console.warn('Tessara: sort failed', e);
        setActiveChip('newest');
      }
    }

    function setActiveChip(sortBy) {
      document.querySelectorAll('.tds-chip[data-sort]').forEach(function (chip) {
        var active = chip.dataset.sort === sortBy;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    document.querySelectorAll('.tds-chip[data-sort]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var sortBy = chip.dataset.sort;
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortBy);
        window.history.pushState({}, '', url.toString());
        sortGrid(sortBy);
        setActiveChip(sortBy);
        window.tessara && window.tessara.track('select_content', {
          content_type: 'sort',
          item_id: sortBy,
          page_type: 'collections_index',
        });
      });
    });

    var initSort = (new URLSearchParams(window.location.search)).get('sort_by') || 'newest';
    sortGrid(initSort);
    setActiveChip(initSort);

    var items = Array.from(document.querySelectorAll('[data-handle]')).map(function (el, i) {
      return {
        item_id: el.dataset.handle,
        item_name: el.dataset.title,
        item_list_position: i + 1,
        item_brand: 'Tessara',
        item_category: 'Collection',
        quantity: parseInt(el.dataset.productCount || 0),
      };
    });
    window.tessara && window.tessara.track('view_item_list', {
      item_list_id: 'collections_index',
      item_list_name: 'Collections index',
      items: items,
      page_type: 'collections_index',
    });

    document.querySelectorAll('.tds-collection-card').forEach(function (card, i) {
      card.addEventListener('click', function () {
        var wrapper = card.closest('[data-handle]');
        if (!wrapper) return;
        window.tessara && window.tessara.track('select_item', {
          item_list_id: 'collections_index',
          item_list_name: 'Collections index',
          item_id: wrapper.dataset.handle,
          item_name: wrapper.dataset.title,
          item_list_position: i + 1,
          item_brand: 'Tessara',
          item_category: 'Collection',
          page_type: 'collections_index',
        });
        var badge = card.querySelector('.tds-badge--new');
        if (badge) {
          window.tessara && window.tessara.track('select_promotion', {
            promotion_id: 'collection_' + wrapper.dataset.handle + '_new',
            promotion_name: 'New collection: ' + wrapper.dataset.title,
            creative_slot: 'collections_index',
            location_id: 'collections_index',
          });
        }
      });
    });

    var newBadges = document.querySelectorAll('.tds-badge--new');
    if (newBadges.length && 'IntersectionObserver' in window) {
      var badgeFired = false;
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!badgeFired && entry.isIntersecting) {
            badgeFired = true;
            var wrapper = entry.target.closest('[data-handle]');
            if (wrapper) {
              window.tessara && window.tessara.track('view_promotion', {
                promotion_id: 'collection_' + wrapper.dataset.handle + '_new',
                promotion_name: 'New collection: ' + wrapper.dataset.title,
                creative_slot: 'collections_index',
                location_id: 'collections_index',
              });
            }
          }
        });
      });
      newBadges.forEach(function (b) { obs.observe(b); });
    }

    // ── Mockup hover reveal ────────────────────────────────────
    document.querySelectorAll('.tds-collection-card__hero').forEach(function (hero) {
      var mockups = (hero.dataset.mockups || '').split('|').filter(Boolean);
      if (!mockups.length) return;
      var overlay = hero.querySelector('.tds-collection-card__hero-mockup');
      if (!overlay) return;
      hero.addEventListener('mouseenter', function () {
        var url = mockups[Math.floor(Math.random() * mockups.length)];
        overlay.style.backgroundImage = 'url(\'' + url + '\')';
        overlay.classList.add('is-visible');
      });
      hero.addEventListener('mouseleave', function () {
        overlay.classList.remove('is-visible');
      });
    });

  }());
}

/* ════════════════════════════════════════════════════════════
   TessaraSort — client-side sort for pill-sorted lists.
   Used by: collections-landing (?view=landing)

   Sort keys:
     'newest'        — data-created (Unix timestamp) desc
     'alpha'         — data-title asc
     'most_patterns' — data-pattern-count desc
   ════════════════════════════════════════════════════════════ */

const TessaraSort = (() => {
  'use strict';

  function sortItems(container, key) {
    const items = Array.from(container.children);

    items.sort((a, b) => {
      if (key === 'newest') {
        return Number(b.dataset.created || 0) - Number(a.dataset.created || 0);
      }
      if (key === 'alpha') {
        return (a.dataset.title || '').localeCompare(b.dataset.title || '');
      }
      if (key === 'most_patterns') {
        return Number(b.dataset.patternCount || 0) - Number(a.dataset.patternCount || 0);
      }
      return 0;
    });

    items.forEach((item) => container.appendChild(item));
  }

  function registerSortable(container, defaultKey = 'newest') {
    if (!container) return;

    const pills      = document.querySelectorAll('.sort-pill');
    const params     = new URLSearchParams(window.location.search);
    const initialKey = params.get('sort') || defaultKey;

    sortItems(container, initialKey);

    pills.forEach((pill) => {
      const active = pill.dataset.sort === initialKey;
      pill.classList.toggle('active', active);
      pill.setAttribute('aria-pressed', String(active));
    });

    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const key = pill.dataset.sort;
        sortItems(container, key);

        pills.forEach((p) => {
          const isActive = p === pill;
          p.classList.toggle('active', isActive);
          p.setAttribute('aria-pressed', String(isActive));
        });

        const url = new URL(window.location.href);
        url.searchParams.set('sort', key);
        history.replaceState(null, '', url.toString());
      });
    });
  }

  return { registerSortable };
})();

/* Register sort target for the collections landing page. */
if (document.querySelector('.list-collections-landing')) {
  TessaraSort.registerSortable(
    document.querySelector('.list-collections-landing .featured'),
    'newest'
  );
}
